import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  MenuItem,
  Button,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { CalculatorTextFieldItem } from "../components/CalculatorTextFieldItem";
import CalculatorSelectFieldItem from "../components/CalculatorSelectFieldItem";

type ERSPCVersion = "3" | "4" | "6";

export default function ERSPCComponent() {
  const [version, setVersion] = useState<ERSPCVersion>("3");
  const [psa, setPsa] = useState<string>("");
  const [dre, setDre] = useState<string>("normal");
  const [volume, setVolume] = useState<string>("");
  const [priorBiopsy, setPriorBiopsy] = useState<string>("none");
  const [trus, setTrus] = useState<string>("normal");
  const [result, setResult] = useState<string | null>(null);

  const calculate = () => {
    const psaVal = parseFloat(psa);
    const volVal = parseFloat(volume);

    if (
      Number.isNaN(psaVal) ||
      Number.isNaN(volVal) ||
      psaVal <= 0 ||
      volVal <= 0
    ) {
      setResult(
        "Please enter valid numeric values for PSA and Prostate Volume.",
      );
      return;
    }

    if (version === "3") {
      // ERSPC RC3 (Biopsy-naive) Formula (Approximate)
      // lp = -1.826 + 1.024 * (log2(PSA) - 2.0) - 1.50 * (log2(volVal/25)) + 0.992 * (DRE)
      // Note: This is a simplified reconstruction for demonstration.
      const isDreAbnormal = dre === "abnormal" ? 1 : 0;
      const log2PSA = Math.log2(psaVal);
      const log2Vol = Math.log2(volVal);

      // Using a standardized version of the formula found in literature
      let lp = -1.826;
      lp += 1.024 * (log2PSA - 2.0);
      lp -= 1.5 * (log2Vol - 4.64); // 4.64 is approx log2(25)
      lp += 0.992 * isDreAbnormal;

      const prob = (Math.exp(lp) / (1 + Math.exp(lp))) * 100;
      setResult(
        `Estimated Risk of Prostate Cancer (RC3): ${prob.toFixed(1)}%.`,
      );
    } else {
      setResult(
        `Inputs captured for ERSPC RC${version}. ` +
          `Due to the proprietary nature of RC4/RC6 models, please verify the final percentage at prostatecancer-riskcalculator.com.`,
      );
    }
  };

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Box flex={1} overflow="auto" p={2}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The ERSPC Risk Calculators (3, 4, 6) assess the risk of prostate
          cancer based on clinical findings.
        </Typography>

        <Box sx={{ mb: 3, display: "flex", justifyContent: "center" }}>
          <ToggleButtonGroup
            value={version}
            exclusive
            onChange={(_, next) => next && setVersion(next)}
            size="small"
          >
            <ToggleButton value="3">RC3 (Biopsy-Naive)</ToggleButton>
            <ToggleButton value="4">RC4 (Prior Biopsy)</ToggleButton>
            <ToggleButton value="6">RC6 (Screening)</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Grid container spacing={2}>
          <CalculatorTextFieldItem
            text="PSA (ng/ml)"
            value={psa}
            onChange={(e) => setPsa(e.target.value)}
          />
          <CalculatorTextFieldItem
            text="Prostate Volume (cm³)"
            value={volume}
            onChange={(e) => setVolume(e.target.value)}
          />
          <CalculatorSelectFieldItem
            text="DRE Finding"
            value={dre}
            onChange={(e) => setDre(e.target.value)}
          >
            <MenuItem value="normal">Normal</MenuItem>
            <MenuItem value="abnormal">Abnormal</MenuItem>
          </CalculatorSelectFieldItem>

          {version === "4" && (
            <CalculatorSelectFieldItem
              text="Prior Negative Biopsies"
              value={priorBiopsy}
              onChange={(e) => setPriorBiopsy(e.target.value)}
            >
              <MenuItem value="1">1</MenuItem>
              <MenuItem value="2">2 or more</MenuItem>
            </CalculatorSelectFieldItem>
          )}

          {version === "6" && (
            <CalculatorSelectFieldItem
              text="TRUS Finding"
              value={trus}
              onChange={(e) => setTrus(e.target.value)}
            >
              <MenuItem value="normal">Normal</MenuItem>
              <MenuItem value="abnormal">Abnormal</MenuItem>
            </CalculatorSelectFieldItem>
          )}
        </Grid>

        <Button
          variant="contained"
          onClick={calculate}
          sx={{ mt: 2, mb: 2 }}
          fullWidth
        >
          Calculate Risk
        </Button>
      </Box>

      <Box
        p={2}
        sx={{
          borderTop: 1,
          borderColor: "#D3D3D3",
        }}
      >
        <Typography variant="h5">Result</Typography>
        {!result ? (
          <Typography>
            <i>Please fill out the fields and click Calculate</i>
          </Typography>
        ) : (
          <>
            <Typography variant="body1">{result}</Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              *Disclaimer: These models are proprietary. This tool provides an
              estimation for RC3 and captured inputs for RC4/RC6. Always verify
              with official ERSPC tools.
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
}
